import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {BaseMasonryGridProps} from '@Components/base-masonry-grid';
import {BaseMasonryGrid} from '@Components/base-masonry-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridButtonItemStorage} from '@Components/base-grid/components/grid-button-item';
import {getRandomNumberInRange} from '@Utils/number.util';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {v4 as uuidv4} from 'uuid';
import type {LazyLoadingGetterParams, LazyLoadingOffsets, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {SKELETON_ITEMS_LENGTH} from '@Components/lazy-masonry-grid/lazy-masonry-grid.types';
import {addItemsToGrid, addLazyGrid, deleteGridItems, reloadGrid} from '../base-grid';
import {useAppDispatch, useAppSelector} from '@/hooks';

const DEFAULT_LOAD_ITEM_BATCH_SIZE = 99;
const BOTTOM_SCROLL_OFFSET = 100;
const SKELETON_DIMENSIONS_RANGE = {
  minWidth: 80,
  maxWidth: 200,
  minHeight: 70,
  maxHeight: 90,
};

export interface LazyMasonryGridProps extends BaseMasonryGridProps {
  startLoadingItems?: boolean;
  clearGridOnUnmount?: boolean;
  loadItemBatchSize?: number;
  showLoadingSkeletonItems?: boolean;

  getItems(getterParams: LazyLoadingGetterParams): Promise<LazyLoadingResponse>;

  getPrefixItems?(): Promise<GridButtonItemStorage[]>;

  onAllItemsLoaded?(): void;

  initialItemsBatchLoaded?(): void;

  gridRef?(ref: HTMLDivElement | null): void;
  doCheckForNextBatchImmediately?: boolean;
}

export function LazyMasonryGrid({
  loadItemBatchSize = DEFAULT_LOAD_ITEM_BATCH_SIZE,
  onAllItemsLoaded = (): void => {},
  initialItemsBatchLoaded = (): void => {},
  startLoadingItems = true,
  clearGridOnUnmount = false,
  gridRef = $.noop.bind(null),
  scrollingContainer = null,
  showLoadingSkeletonItems = true,
  doCheckForNextBatchImmediately = true,
  ...props
}: LazyMasonryGridProps): ReactElement {
  const gridData = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId];
  });
  const gridContentEl = useRef<HTMLDivElement | null>(null);
  const loadingBatch = useRef(false);
  const offset = useRef<LazyLoadingOffsets>({});
  const loadMore = useRef(true);
  const currentRequestNum = useRef<number>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      addLazyGrid({
        id: props.gridId,
        maxSelectionNumber: props.maxSelectionNumber,
        gridSelectionGroupId: props.gridSelectionGroupId,
      })
    );

    return () => {
      if (clearGridOnUnmount) {
        dispatch(
          reloadGrid({
            gridId: props.gridId,
            gridSelectionGroupId: props.gridSelectionGroupId ?? props.gridId,
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    if (startLoadingItems && gridData?.requestNum && gridData.requestNum !== currentRequestNum.current) {
      loadingBatch.current = false;
      loadMore.current = true;
      offset.current = {};
      currentRequestNum.current = gridData.requestNum as number;
      loadInitialItems()
        .then(() => {})
        .catch(() => {});
    }
  }, [gridData?.requestNum, startLoadingItems]);

  const loadInitialItems = async (): Promise<void> => {
    await loadPrefixItems();
    await loadItemBatch();
    initialItemsBatchLoaded();
  };

  const loadPrefixItems = async (): Promise<void> => {
    if (props.getPrefixItems === undefined) {
      return;
    }

    try {
      const items = await props.getPrefixItems();
      if (!gridData.items.length) {
        dispatch(
          addItemsToGrid({
            id: props.gridId,
            items,
            index: 0,
          })
        );
      }
    } catch (e) {
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
        log: false,
      });
    }
  };

  const getSkeletonItems = (skeletonItemIds: Array<string>): Array<GridSkeletonItemStorage> => {
    return skeletonItemIds.map((id) => {
      return {
        id,
        type: GRID_ITEM_TYPE.SKELETON,
        width: getRandomNumberInRange(SKELETON_DIMENSIONS_RANGE.minWidth, SKELETON_DIMENSIONS_RANGE.maxWidth),
        height: getRandomNumberInRange(SKELETON_DIMENSIONS_RANGE.minHeight, SKELETON_DIMENSIONS_RANGE.maxHeight),
      } as GridSkeletonItemStorage;
    });
  };

  const loadItemBatch = async (): Promise<void> => {
    if (!loadMore.current || loadingBatch.current || !currentRequestNum.current) {
      return;
    }

    loadingBatch.current = true;
    const batchRequestNum = currentRequestNum.current;
    const skeletonItemIds = Array.from(Array(SKELETON_ITEMS_LENGTH)).map(() => {
      return uuidv4();
    });
    if (showLoadingSkeletonItems) {
      dispatch(
        addItemsToGrid({
          id: props.gridId,
          items: getSkeletonItems(skeletonItemIds),
        })
      );
    }

    try {
      if (isOldRequest(batchRequestNum)) {
        return;
      }
      const lazyLoadingResponse = await props.getItems({limit: loadItemBatchSize + 1, offsets: offset.current});
      if (lazyLoadingResponse) {
        const {response, offsetsData} = {...lazyLoadingResponse};
        loadMore.current = response.length === loadItemBatchSize + 1;

        if (!loadMore.current) {
          onAllItemsLoaded();
        }
        if (response.length === loadItemBatchSize + 1) {
          response.pop();
        }

        Object.keys(offsetsData).forEach((key) => {
          const value = offsetsData[key];
          if (value === 0) {
            delete offset.current[key];
          } else {
            offset.current[key] = offset.current[key] ? offset.current[key] + value : value;
          }
        });

        if (showLoadingSkeletonItems) {
          dispatch(
            deleteGridItems({
              gridId: props.gridId,
              gridSelectionGroupId: props.gridSelectionGroupId ?? '',
              itemIdsToDelete: skeletonItemIds,
            })
          );
        }

        dispatch(
          addItemsToGrid({
            id: props.gridId,
            items: response as GridItemStorage[],
          })
        );
        loadingBatch.current = false;
        setTimeout(() => {
          if (canShowMoreItemsInView() && doCheckForNextBatchImmediately) {
            void loadItemBatch();
          }
        }, 200);
      }
    } catch (e) {
      console.error(e);
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
        log: false,
      });
    }
  };

  const getScrollingContainer = (): HTMLDivElement | null => {
    if (scrollingContainer) {
      return scrollingContainer;
    }
    return gridContentEl.current;
  };

  const canShowMoreItemsInView = (): boolean => {
    const scrollingContainerEl = getScrollingContainer();
    return !!(
      loadMore.current &&
      scrollingContainerEl &&
      scrollingContainerEl.offsetHeight &&
      scrollingContainerEl.scrollHeight - scrollingContainerEl.offsetHeight < BOTTOM_SCROLL_OFFSET
    );
  };

  const isOldRequest = (requestNum: number): boolean => {
    if (!currentRequestNum.current) {
      return false;
    }
    return requestNum < currentRequestNum.current;
  };

  return (
    <BaseMasonryGrid
      {...props}
      data-test-load-more={loadMore.current}
      scrollingContainer={getScrollingContainer()}
      onScrolledToBottom={(): void => {
        void loadItemBatch();
      }}
      gridRef={(ref): void => {
        gridRef(ref);
        gridContentEl.current = ref;
      }}
    />
  );
}
